// LT-255 — Top-level Calls list. Restores the "show me all my calls"
// entry point that disappeared when LT-179's nav refactor deprecated the
// Campaigns/Runs tree in favour of Sequences. Backed by the LT-253 DB
// rewrite of GET /calls/.
//
// Rows drill into the existing TranscriptViewerPage at
// /calls/:callSummaryId/transcript — the same target ContactDetailPage
// uses via LT-254.
//
// DS v1 migration (post-LT-308): filter chrome → <Card>, native filter
// controls → <Input>/<Select>, list rows → <DataTable>, pagination/CTA
// buttons → <Button>, status chips → <Pill>.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PhoneCall, PhoneIncoming } from 'lucide-react';

import PageLoader from '../../components/PageLoader';
import PageShell from '../../components/PageShell';
import { APP_NAME } from '../../config/region';
import {
  Button,
  Card,
  DataTable,
  EmptyState,
  Input,
  Pill,
  Select,
  type ColumnDef,
  type PillIntent,
} from '../../ui';
import {
  callsAPIv2,
  CallListFilters,
  CallSummaryListItem,
  CallSummaryListResponse,
} from '../../services/callsAPI';

const PAGE_SIZE = 25;

type DirectionFilter = '' | 'inbound' | 'outbound';


function temperatureIntent(temp?: string | null): PillIntent {
  switch ((temp ?? '').toLowerCase()) {
    case 'hot':
      return 'danger';
    case 'warm':
      return 'warn';
    case 'cold':
      return 'info';
    case 'qualified':
      return 'accent';
    default:
      return 'neutral';
  }
}


const CallsPage: React.FC = () => {
  const navigate = useNavigate();
  const [calls, setCalls] = useState<CallSummaryListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const [page, setPage] = useState(0);
  const [direction, setDirection] = useState<DirectionFilter>('');
  const [disposition, setDisposition] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    const filters: CallListFilters = {
      skip: page * PAGE_SIZE,
      limit: PAGE_SIZE,
    };
    if (direction) filters.direction = direction;
    if (disposition.trim()) filters.disposition = disposition.trim();
    try {
      const res: CallSummaryListResponse = await callsAPIv2.list(filters);
      setCalls(res.calls);
      setTotal(res.total);
    } catch (e: any) {
      setErr(e?.response?.data?.detail?.message || e?.message || 'Failed to load calls');
    } finally {
      setLoading(false);
    }
  }, [page, direction, disposition]);

  useEffect(() => {
    load();
  }, [load]);

  const pageCount = useMemo(() => Math.max(1, Math.ceil(total / PAGE_SIZE)), [total]);

  const contactName = (c: CallSummaryListItem): string => {
    const first = c.contact_first_name ?? '';
    const last = c.contact_last_name ?? '';
    const joined = `${first} ${last}`.trim();
    return joined || c.contact_phone || '(unknown contact)';
  };

  const columns: ColumnDef<CallSummaryListItem>[] = useMemo(
    () => [
      {
        id: 'direction',
        header: '',
        className: 'w-8',
        accessor: (c) => {
          const Icon = c.call_direction === 'inbound' ? PhoneIncoming : PhoneCall;
          return <Icon className="w-4 h-4 text-slate-400" aria-hidden />;
        },
      },
      {
        id: 'contact',
        header: 'Contact',
        accessor: (c) => (
          <div className="min-w-0">
            <div className="text-sm font-medium text-slate-50 truncate">
              {contactName(c)}
            </div>
            {c.contact_phone ? (
              <div className="text-xs text-slate-400 font-mono mt-0.5 truncate">
                {c.contact_phone}
              </div>
            ) : null}
          </div>
        ),
      },
      {
        id: 'disposition',
        header: 'Disposition',
        accessor: (c) =>
          c.disposition ? (
            <Pill intent="neutral">{c.disposition}</Pill>
          ) : (
            <span className="text-slate-500">—</span>
          ),
      },
      {
        id: 'temperature',
        header: 'Temperature',
        accessor: (c) =>
          c.lead_temperature ? (
            <Pill intent={temperatureIntent(c.lead_temperature)}>
              {c.lead_temperature}
            </Pill>
          ) : (
            <span className="text-slate-500">—</span>
          ),
      },
      {
        id: 'summary',
        header: 'Summary',
        accessor: (c) =>
          c.analysis_summary ? (
            <span className="text-slate-300 line-clamp-2">{c.analysis_summary}</span>
          ) : (
            <span className="text-slate-500">—</span>
          ),
      },
      {
        id: 'when',
        header: 'When',
        sortable: true,
        sortValue: (c) => new Date(c.timestamp).getTime(),
        accessor: (c) => (
          <span className="text-xs text-slate-400">
            {new Date(c.timestamp).toLocaleString()}
          </span>
        ),
      },
    ],
    [],
  );

  return (
    <PageShell>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-xl font-semibold text-slate-50">Calls</h1>
          <p className="text-xs text-slate-400 mt-1">
            Every call summary + transcript, newest first. Click a row to view the transcript and analysis.
          </p>
        </div>
        <div className="text-sm text-slate-400">
          {total === 0 ? 'no calls' : `${total} total`}
        </div>
      </div>

      <Card padding="default" className="mb-4">
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <label
              htmlFor="calls-direction-filter"
              className="block text-xs text-slate-400 mb-1"
            >
              Direction
            </label>
            <Select
              id="calls-direction-filter"
              value={direction}
              onChange={(e) => {
                setDirection(e.target.value as DirectionFilter);
                setPage(0);
              }}
            >
              <option value="">All</option>
              <option value="outbound">Outbound</option>
              <option value="inbound">Inbound</option>
            </Select>
          </div>
          <div>
            <label
              htmlFor="calls-disposition-filter"
              className="block text-xs text-slate-400 mb-1"
            >
              Disposition
            </label>
            <Input
              id="calls-disposition-filter"
              type="text"
              value={disposition}
              placeholder="e.g. qualified"
              onChange={(e) => setDisposition(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setPage(0);
                  load();
                }
              }}
            />
          </div>
        </div>
      </Card>

      {err && (
        <div className="bg-danger-tint border border-danger-border text-danger-text px-4 py-3 rounded-ds-lg mb-4">
          {err}
        </div>
      )}

      {loading ? (
        <PageLoader message="Loading calls..." />
      ) : calls.length === 0 ? (
        // LT-314: DS-styled empty state. Primary CTA sends users to the
        // Campaigns page to launch one that produces calls.
        <EmptyState
          surface="calls_list"
          icon={PhoneCall}
          headline="No calls to show"
          subhead={`Launch a campaign and ${APP_NAME} will record every outbound and inbound call here with transcripts and call intelligence.`}
          primary={{ label: 'Go to Campaigns', to: '/campaigns' }}
        />
      ) : (
        <>
          <DataTable<CallSummaryListItem>
            data={calls}
            columns={columns}
            getRowKey={(c) => c.id}
            ariaLabel="Calls"
            onRowClick={(c) => navigate(`/calls/${c.id}`)}
          />

          <div className="flex items-center justify-between mt-3 px-1">
            <div className="text-xs text-slate-400">
              Page {page + 1} of {pageCount}
            </div>
            <div className="flex gap-2">
              <Button
                intent="secondary"
                size="sm"
                disabled={page === 0 || loading}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
              >
                Previous
              </Button>
              <Button
                intent="secondary"
                size="sm"
                disabled={page + 1 >= pageCount || loading}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        </>
      )}
    </PageShell>
  );
};

export default CallsPage;
